<template>
  <div>
    <h4 class="table-page-header">
      Caregivers
      <el-tooltip v-if="!isCaregiver" content="Add New Caregiver">
        <el-button type="text" @click="createCaregiver">
          <i class="fa-solid fa-circle-plus" style="font-size: 22px"></i>
        </el-button>
      </el-tooltip>
    </h4>
    <div class="filter-section">
      <div class="filter-row">
        <div class="filter-group search-group">
          <el-input
            v-model="searchQuery"
            placeholder="Search"
            clearable
            @clear="clearSearch"
            @input="onSearch"
            class="search-input"
          >
            <i
              slot="prepend"
              class="fa fa-search"
              style="padding-left: 4px; padding-right: 4px"
            ></i>
          </el-input>
        </div>
        <div class="filter-group">
          <AIVSelect
            v-model="activeStatusFilter"
            :options="activeStatusOptions"
            placeholder="Filter by status"
            @change="onStatusFilterChange"
            class="compact-filter"
          />
        </div>
        <div class="filter-group">
          <AIVSelect
            v-model="adminFilter"
            :options="adminFilterOptions"
            placeholder="Filter by admin"
            @change="onAdminFilterChange"
            class="compact-filter"
          />
        </div>
      </div>
    </div>
    <AIVTable
      :columns="caregiverColumns"
      :data="caregivers"
      :pagination="pagination"
      :item-name="'caregivers'"
      :row-clickable="true"
      @pageChange="fetchCaregivers"
      @pageSizeChange="handlePageSizeChange"
      @sort-change="handleSortChange"
      @row-click="handleRowClick"
    >
      <template #cell-actions="{ row }">
        <button
          v-if="isAdmin && row.active"
          class="btn btn-sm btn-primary mr-2"
          @click.stop="openAdminPasswordModal(row)"
          title="Change Password"
        >
          <i class="fa-solid fa-key"></i>
        </button>
        <button
          v-if="!isCaregiver"
          :class="
            row.active ? 'btn btn-sm btn-danger' : 'btn btn-sm btn-success'
          "
          @click.stop="
            $confirmAction(
              () => toggleCaregiverStatus(row),
              row.active
                ? `Are you sure you want to deactivate ${row.first_name} ${row.last_name}? They will no longer be able to login to the Oppo app.`
                : `Are you sure you want to activate ${row.first_name} ${row.last_name}? They will be able to login to the Oppo app again.`,
            )
          "
          :title="row.active ? 'Deactivate' : 'Activate'"
        >
          <i
            :class="
              row.active ? 'fa-solid fa-user-slash' : 'fa-solid fa-user-check'
            "
          ></i>
        </button>
      </template>
    </AIVTable>
    <AIVDialog
      v-model="$modal.state.create_or_edit_caregiver"
      title="Create a New Caregiver"
      height="80vh"
      class="caregiver-edit-modal"
    >
      <el-form
        ref="createForm"
        :model="caregiver"
        label-position="top"
        label-width="120px"
        class="full-width-form"
        autocomplete="off"
      >
        <AIVRow
          :record.sync="caregiver"
          :fields="[
            { label: 'First Name', key: 'first_name' },
            { label: 'Last Name', key: 'last_name' },
            { label: 'Email', key: 'email' },
          ]"
        />
        <AIVRow
          :record.sync="caregiver.address"
          :fields="[
            { label: 'Address1', key: 'address1' },
            { label: 'Address2', key: 'address2' },
          ]"
        />
        <AIVRow
          :record.sync="caregiver.address"
          :fields="[
            { label: 'City', key: 'city' },
            { label: 'State', key: 'state' },
            { label: 'Zip', key: 'zip' },
          ]"
        />
        <AIVRow
          :record.sync="caregiver"
          :fields="[
            { label: 'Mobile Phone', key: 'mobile_phone' },
            { label: 'Other Phone', key: 'other_phone' },
          ]"
        />
        <AIVRow
          :record.sync="caregiver"
          :fields="[
            { label: 'Title', key: 'title' },
            {
              label: 'Business Unit',
              key: 'business_unit',
              type: 'select',
              options: businessUnitOptions,
              placeholder: 'Select Business Unit',
            },
            { label: 'Position ID', key: 'position_id' },
          ]"
        />
        <AIVRow
          :record.sync="caregiver"
          :fields="
            [
              {
                label: 'User Type',
                key: 'user_type',
                type: 'select',
                options: userTypeOptions,
                autocomplete: 'off',
              },
              caregiver.user_type === 'Caregiver' ||
              caregiver.user_type === 'Employment Specialist'
                ? {
                    label:
                      caregiver.user_type === 'Employment Specialist'
                        ? 'Manager'
                        : 'Lead',
                    key: 'id_lead',
                    type: 'select',
                    options: leadOptions,
                    placeholder:
                      caregiver.user_type === 'Employment Specialist'
                        ? 'Select Manager'
                        : 'Select Lead',
                    autocomplete: 'off',
                  }
                : null,
              {
                label: 'Admin',
                key: 'id_admin',
                type: 'select',
                options: adminOptions,
                placeholder: 'Select Admin',
                autocomplete: 'off',
              },
            ].filter(Boolean)
          "
        />

        <div class="row mb-3 w-100 mx-0">
          <div class="col-md-4 px-2">
            <label class="d-block">Password</label>
            <div class="password-input-wrapper">
              <el-input
                v-model="caregiver.password"
                :type="passwordVisible ? 'text' : 'password'"
                placeholder="Enter password"
                autocomplete="new-password"
                class="w-100"
              ></el-input>
              <i
                :class="
                  passwordVisible ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'
                "
                @click="togglePasswordVisibility"
                class="password-toggle-icon"
                aria-hidden="true"
              ></i>
            </div>
          </div>
          <div class="col-md-4 px-2">
            <label class="d-block">Confirm Password</label>
            <div class="password-input-wrapper">
              <el-input
                v-model="caregiver.confirmPassword"
                :type="confirmPasswordVisible ? 'text' : 'password'"
                placeholder="Confirm password"
                autocomplete="new-password"
                class="w-100"
              ></el-input>
              <i
                :class="
                  confirmPasswordVisible
                    ? 'fa-solid fa-eye-slash'
                    : 'fa-solid fa-eye'
                "
                @click="toggleConfirmPasswordVisibility"
                class="password-toggle-icon"
                aria-hidden="true"
              ></i>
            </div>
            <span class="w-100 d-block">
              <span
                v-if="passwordError"
                class="text-danger"
                style="font-size: 13px"
                >{{ passwordError }}</span
              >
            </span>
          </div>
          <div class="col-md-4 px-2">
            <label class="d-block">Password Requirements</label>
            <div class="password-requirements">
              <ul>
                <li>At least 8 characters</li>
                <li>At least one number</li>
                <li>At least one special character</li>
              </ul>
            </div>
          </div>
        </div>
      </el-form>
      <template slot="footer">
        <div class="text-center">
          <el-button round @click="$modal.close('create_or_edit_caregiver')"
            >Cancel</el-button
          >
          <el-button type="primary" round @click="saveCaregiver">
            Create
          </el-button>
        </div>
      </template>
    </AIVDialog>

    <AIVDialog
      v-model="$modal.state.view_caregiver_schedule"
      :title="`${selectedCaregiverName}'s Schedule`"
      width="90%"
      :height="activeScheduleTab === 'edit' ? '85vh' : '100vh'"
      class="caregiver-schedule-modal"
    >
      <div class="schedule-calendar-container">
        <!-- Schedule Type Toggle -->
        <div
          v-if="!isCaregiver"
          class="schedule-type-toggle-section"
        >
          <div class="button-toggle-row">
            <el-button
              :type="activeScheduleTab === 'calendar' ? 'primary' : 'default'"
              @click="activeScheduleTab = 'calendar'"
              class="toggle-button"
            >
              Calendar
            </el-button>
            <el-button
              :type="activeScheduleTab === 'edit' ? 'primary' : 'default'"
              @click="activeScheduleTab = 'edit'"
              class="toggle-button"
            >
              Work Schedule
            </el-button>
          </div>
        </div>

        <!-- Calendar View -->
        <div v-if="activeScheduleTab === 'calendar'" class="schedule-view">
          <div class="modern-calendar-wrapper">
            <!-- Modern Legend -->
            <div class="modern-legend-row">
              <div class="legend-groups">
                <div class="legend-group">
                  <div class="legend-group-header">
                    <i class="fas fa-calendar-check"></i>
                    <span>Schedule:</span>
                  </div>
                  <div class="legend-item">
                    <div class="legend-color work-schedule"></div>
                    <span>Work Hours</span>
                  </div>
                  <div class="legend-item">
                    <div class="legend-color time-off"></div>
                    <span>Time Off</span>
                  </div>
                </div>
                <div class="legend-group">
                  <div class="legend-group-header">
                    <i class="fas fa-info-circle"></i>
                    <span>Shifts:</span>
                  </div>
                  <div class="legend-item">
                    <div class="legend-color status-scheduled"></div>
                    <span>Scheduled</span>
                  </div>
                  <div class="legend-item">
                    <div class="legend-color status-clocked-in"></div>
                    <span>Clocked In</span>
                  </div>
                  <div class="legend-item">
                    <div class="legend-color status-clocked-out"></div>
                    <span>Clocked Out</span>
                  </div>
                  <div class="legend-item">
                    <div class="legend-color status-approved"></div>
                    <span>Approved</span>
                  </div>
                  <div class="legend-item">
                    <div class="legend-color status-missed"></div>
                    <span>Missed</span>
                  </div>
                </div>
              </div>
            </div>
            <!-- Month Navigation Controls -->
            <div class="month-navigation">
              <button @click="goToPreviousMonth" class="nav-btn">
                <i class="fas fa-chevron-left"></i>
              </button>
              <div class="current-month-display">
                {{ currentMonthYear }}
              </div>
              <button @click="goToNextMonth" class="nav-btn">
                <i class="fas fa-chevron-right"></i>
              </button>
            </div>
            <div class="calendar-view">
              <MonthView
                v-if="!loadingShifts"
                :currentMonth="currentMonth"
                :daysInMonth="daysInMonth"
                :dayNames="dayNames"
                :eventsForDate="shiftsForDate"
                :isToday="isToday"
                :workSchedule="selectedCaregiverSchedule"
                :getScheduledHours="getScheduledHoursForDate"
                :approvedTimeOff="caregiverApprovedTimeOff"
                @show-details="viewShiftDetails"
              />
              <div v-else class="text-center p-4">
                <i class="fa fa-spinner fa-spin"></i> Loading schedule...
              </div>
            </div>
          </div>
        </div>

        <!-- Work Schedule View -->
        <div v-if="activeScheduleTab === 'edit'" class="schedule-view">
          <div class="schedule-editor">
            <!-- Schedule Management -->
            <div
              class="schedule-management"
              v-if="
                activeAndFutureSchedules.length > 0 || selectedCaregiverSchedule.id
              "
            >
              <div class="schedule-selector">
                <label class="time-label">Select Schedule</label>
                <div class="schedule-tabs">
                  <button
                    v-for="schedule in activeAndFutureSchedules"
                    :key="schedule.id"
                    @click="selectSchedule(caregiverSchedules.indexOf(schedule))"
                    :class="[
                      'schedule-tab',
                      { active: selectedScheduleIndex === caregiverSchedules.indexOf(schedule) },
                    ]"
                  >
                    {{ formatScheduleLabel(schedule) }}
                  </button>
                  <button
                    @click="createNewSchedule"
                    class="schedule-tab new-schedule"
                  >
                    + New Schedule
                  </button>
                </div>
              </div>
              <div class="schedule-actions" v-if="selectedCaregiverSchedule.id">
                <button
                  @click="duplicateCurrentSchedule"
                  class="action-btn duplicate-btn"
                  title="Duplicate this schedule"
                >
                  <i class="fa fa-copy"></i> Duplicate
                </button>
                <button
                  @click="deleteCurrentSchedule"
                  class="action-btn delete-btn"
                  title="Delete this schedule"
                  v-if="caregiverSchedules.length > 1"
                >
                  <i class="fa fa-trash"></i> Delete
                </button>
              </div>
            </div>

            <!-- Date Range Selection -->
            <div class="schedule-date-range">
              <div class="date-range-grid">
                <div class="date-input-group">
                  <label class="time-label">Start Date</label>
                  <input
                    type="date"
                    v-model="selectedCaregiverSchedule.start_date"
                    :min="todayDate"
                    class="date-input"
                  />
                </div>
                <div class="date-input-group">
                  <label class="time-label">End Date (Optional)</label>
                  <input
                    type="date"
                    v-model="selectedCaregiverSchedule.end_date"
                    :min="selectedCaregiverSchedule.start_date || todayDate"
                    class="date-input"
                  />
                </div>
              </div>
            </div>

            <!-- Timezone Display -->
            <div class="schedule-date-range" style="margin-top: 10px;">
              <div class="date-range-grid">
                <div class="date-input-group" style="max-width: 350px;">
                  <label class="time-label">Timezone</label>
                  <div v-if="inferredCaregiverTimezone" class="date-display">
                    {{ formatTimezoneLabel(inferredCaregiverTimezone) }}
                  </div>
                  <AIVSelect
                    v-else
                    :value="scheduleTimezone"
                    @input="scheduleTimezone = $event"
                    :options="timezoneOptions"
                    placeholder="Select timezone (required)"
                    :custom-style="{ backgroundColor: '#656565', minWidth: '100%' }"
                  />
                </div>
              </div>
              <div v-if="showScheduleTimezoneWarning" style="color: red; font-weight: bold; margin-top: 5px; font-size: 13px;">
                ⚠ Caregiver timezone ({{ formatTimezoneLabel(scheduleTimezone) }}) differs from your browser timezone ({{ formatTimezoneLabel(browserTimezone) }}).
              </div>
            </div>

            <div class="schedule-calendar-grid">
              <!-- Day headers with checkboxes row (Sun through Sat) -->
              <div
                v-for="dayConfig in dayConfigs"
                :key="dayConfig.key + '-header'"
                class="schedule-day-header"
              >
                <label class="header-checkbox-label">
                  <span>{{ dayConfig.short }}</span>
                  <input
                    type="checkbox"
                    :checked="
                      selectedCaregiverSchedule[dayConfig.key + '_enabled']
                    "
                    @change="
                      onDayEnabledChange(dayConfig.key, $event.target.checked)
                    "
                  />
                  <span class="schedule-checkmark"></span>
                </label>
              </div>

              <!-- Day cells with multiple shift support -->
              <div
                v-for="dayConfig in dayConfigs"
                :key="dayConfig.key + '-cell'"
                class="schedule-day-cell"
              >
                <div
                  class="day-content"
                  :class="{
                    'day-enabled':
                      selectedCaregiverSchedule[dayConfig.key + '_enabled'],
                  }"
                >
                  <div
                    v-if="selectedCaregiverSchedule[dayConfig.key + '_enabled']"
                    class="multiple-shifts-controls"
                  >
                    <div
                      v-for="(shift, shiftIndex) in getShiftsForDay(
                        dayConfig.key,
                      )"
                      :key="`${dayConfig.key}-shift-${shiftIndex}`"
                      class="shift-row"
                    >
                      <div class="shift-times">
                        <div class="time-input-group">
                          <label class="time-label">Start</label>
                          <AIVTimeSelect
                            :value="extractTimeFromDateTime(shift.start)"
                            @input="
                              updateShiftTime(
                                dayConfig.key,
                                shiftIndex,
                                'start',
                                $event,
                              )
                            "
                            :picker-options="{
                              selectableRange: '00:00:00 - 23:59:59',
                              format: 'HH:mm',
                            }"
                            placeholder="Start"
                            size="mini"
                            format="HH:mm"
                            value-format="HH:mm"
                          />
                        </div>
                        <div class="time-input-group">
                          <label class="time-label">End</label>
                          <AIVTimeSelect
                            :value="extractTimeFromDateTime(shift.end)"
                            @input="
                              updateShiftTime(
                                dayConfig.key,
                                shiftIndex,
                                'end',
                                $event,
                              )
                            "
                            :picker-options="{
                              selectableRange: '00:00:00 - 23:59:59',
                              format: 'HH:mm',
                            }"
                            placeholder="End"
                            size="mini"
                            format="HH:mm"
                            value-format="HH:mm"
                          />
                        </div>
                      </div>
                      <button
                        v-if="getShiftsForDay(dayConfig.key).length > 1"
                        @click="removeShift(dayConfig.key, shiftIndex)"
                        class="remove-shift-btn"
                        title="Remove shift"
                      >
                        <i class="fa fa-times"></i>
                      </button>
                    </div>
                    <button
                      @click="addShift(dayConfig.key)"
                      class="add-shift-btn"
                      title="Add another shift"
                    >
                      <i class="fa fa-plus"></i> Add Shift
                    </button>
                  </div>
                  <div v-else class="day-disabled-text">Off</div>
                </div>
              </div>
            </div>

            <div class="notes-section mt-4">
              <AIVRow
                :record.sync="selectedCaregiverSchedule"
                :fields="[
                  {
                    label: 'Schedule Notes',
                    key: 'notes',
                    type: 'textarea',
                    rows: 3,
                  },
                ]"
              />
            </div>
          </div>
        </div>
      </div>
      <template #footer v-if="activeScheduleTab === 'edit'">
        <div class="text-center">
          <el-button round @click="cancelScheduleEdit">Cancel</el-button>
          <el-button
            type="primary"
            round
            @click="saveSelectedCaregiverSchedule"
          >
            Update Schedule
          </el-button>
        </div>
      </template>
    </AIVDialog>

    <!-- Admin Password Change Modal -->
    <ChangePasswordModal
      v-model="showAdminPasswordModal"
      :is-admin="true"
      :caregiver-id="caregiverForPasswordChange?.id"
      :caregiver-name="caregiverForPasswordChange ? `${caregiverForPasswordChange.first_name} ${caregiverForPasswordChange.last_name}` : ''"
      @success="handleAdminPasswordSuccess"
    />
  </div>
</template>

<script>
import { caregiversService } from "@/serviceHandlers/caregiversService";
import { shiftsService } from "@/serviceHandlers/shiftsService";
import { schedulesService } from "@/serviceHandlers/schedulesService";
import MonthView from "@/pages/Calendar/MonthView.vue";
import AIVTimeSelect from "@/components/AIVTimeSelect.vue";
import ChangePasswordModal from "@/components/ChangePasswordModal.vue";
import { useLoginStore } from "@/stores/loginStore";
import { parseDateOnly, localDateString } from "@/utils/dateOnly";
import { timezoneService } from "@/serviceHandlers/timezoneService";
import {
  TIMEZONE_OPTIONS,
  getBrowserTimezone,
  formatTimezoneLabel,
} from "@/utils/timezoneConstants";
import AIVSelect from "@/components/AIVSelect.vue";

export default {
  name: "Caregivers",
  components: {
    MonthView,
    AIVTimeSelect,
    ChangePasswordModal,
    AIVSelect,
  },
  data() {
    return {
      scheduleTimezone: null,
      inferredCaregiverTimezone: null,
      timezoneOptions: TIMEZONE_OPTIONS,
      passwordError: "",
      passwordVisible: false,
      confirmPasswordVisible: false,
      caregiverColumns: [
        { key: "first_name", label: "First Name" },
        { key: "last_name", label: "Last Name" },
        { key: "title", label: "Title" },
        { key: "lead_name", label: "Lead", class: "hide-on-small" },
        { key: "admin_name", label: "Admin", class: "hide-on-medium" },
        { key: "actions", label: "Actions", sortable: false },
      ],
      newCaregiver: {
        first_name: "",
        last_name: "",
        email: "",
        mobile_phone: "",
        other_phone: "",
        password: "",
        confirmPassword: "",
        user_type: "",
        id_lead: null,
        id_admin: null,
        title: "",
        business_unit: "",
        hourly_pay: "",
        salary: "",
        position_id: "",
        address: {
          address1: "",
          address2: "",
          city: "",
          state: "",
          zip: "",
        },
      },
      searchQuery: "",
      activeStatusFilter: "active",
      activeStatusOptions: [
        { id: "active", name: "Active" },
        { id: "inactive", name: "Inactive" },
      ],
      adminFilter: "",
      caregivers: [],
      pagination: {
        total: 0,
        perPage: 20,
        currentPage: 1,
        totalPages: 0,
      },
      currentStep: 1,
      userTypeOptions: [
        { id: "Lead", name: "Lead" },
        { id: "Caregiver", name: "Caregiver" },
        { id: "Employment Specialist", name: "Employment Specialist" },
      ],
      businessUnitOptions: [
        { id: "VRS - Voc Rehab", name: "VRS - Voc Rehab" },
        {
          id: "CBS - Community Based Services",
          name: "CBS - Community Based Services",
        },
        { id: "HBS - Home Based Services", name: "HBS - Home Based Services" },
        { id: "HH - Host Home Services", name: "HH - Host Home Services" },
      ],
      leadOptions: [],
      adminOptions: [],
      selectedCaregiverName: "",
      caregiverShifts: [],
      loadingShifts: false,
      activeScheduleTab: "calendar",
      selectedCaregiver: null,
      caregiverSchedules: [],
      caregiverApprovedTimeOff: [],
      selectedCaregiverSchedule: {},
      selectedScheduleIndex: 0,
      showAdminPasswordModal: false,
      caregiverForPasswordChange: null,
      currentMonth: new Date(),
      daysInMonth: [],
      dayNames: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
      // Schedule editor data
      scheduleEditorDays: [
        { key: "monday", label: "Monday" },
        { key: "tuesday", label: "Tuesday" },
        { key: "wednesday", label: "Wednesday" },
        { key: "thursday", label: "Thursday" },
        { key: "friday", label: "Friday" },
        { key: "saturday", label: "Saturday" },
        { key: "sunday", label: "Sunday" },
      ],
    };
  },
  computed: {
    browserTimezone() {
      return getBrowserTimezone();
    },
    showScheduleTimezoneWarning() {
      return (
        this.scheduleTimezone &&
        this.scheduleTimezone !== this.browserTimezone
      );
    },
    userRole() {
      const loginStore = useLoginStore();
      return loginStore.user?.user_type || "";
    },
    isCaregiver() {
      return this.userRole === "Caregiver" || this.userRole === "Employment Specialist";
    },
    isAdmin() {
      return this.userRole === "Admin" || this.userRole === "Super Admin";
    },
    adminFilterOptions() {
      return [
        { id: "", name: "All Admins" },
        ...this.adminOptions.map((a) => ({ id: a.id, name: a.name })),
      ];
    },
    currentUserId() {
      const loginStore = useLoginStore();
      return loginStore.user?.id;
    },
    caregiver: {
      get() {
        return this.newCaregiver;
      },
      set(val) {
        this.newCaregiver = val;
      },
    },
    todayDate() {
      return localDateString();
    },
    activeAndFutureSchedules() {
      // Filter out schedules that have already ended
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      return this.caregiverSchedules.filter((schedule) => {
        if (!schedule.end_date) {
          // No end date means it's ongoing
          return true;
        }

        const endDate = new Date(schedule.end_date);
        endDate.setHours(23, 59, 59, 999);

        // Only include if end date is today or in the future
        return endDate >= today;
      });
    },
    dayConfigs() {
      return [
        { key: "sunday", short: "Sun", label: "Sunday" },
        { key: "monday", short: "Mon", label: "Monday" },
        { key: "tuesday", short: "Tue", label: "Tuesday" },
        { key: "wednesday", short: "Wed", label: "Wednesday" },
        { key: "thursday", short: "Thu", label: "Thursday" },
        { key: "friday", short: "Fri", label: "Friday" },
        { key: "saturday", short: "Sat", label: "Saturday" },
      ];
    },
    currentMonthYear() {
      const month = this.currentMonth.toLocaleString("default", {
        month: "long",
      });
      const year = this.currentMonth.getFullYear();
      return `${month} ${year}`;
    },
  },
  async mounted() {
    // Close any open caregiver schedule modal on refresh/reload
    this.$modal.close("view_caregiver_schedule");

    this.updateDaysInMonth();
    await this.fetchCaregivers();
    await this.getLeads();
    await this.getAdmins();
  },
  beforeDestroy() {
    // Ensure modal is closed when component is destroyed
    this.$modal.close("view_caregiver_schedule");
  },
  methods: {
    formatTimezoneLabel,
    async fetchCaregivers(pageNumber = 1) {
      this.$spinner.show();
      try {
        const params = new URLSearchParams();
        params.append("page", pageNumber);
        params.append("perPage", this.pagination.perPage);
        params.append("activeStatus", this.activeStatusFilter);

        if (this.adminFilter) {
          params.append("adminId", this.adminFilter);
        }

        if (this.searchQuery) {
          params.append("searchTerm", this.searchQuery);
        }

        if (this.sortProp) {
          params.append("sortField", this.sortProp);
          params.append("sortOrder", this.sortOrder);
        }

        const response = await caregiversService.listPaginated(params);
        // Process the data to show "None" for unassigned lead/admin, "N/A" for leads
        this.caregivers = response.rows.map((caregiver) => ({
          ...caregiver,
          lead_name:
            caregiver.user_type === "Lead"
              ? "N/A"
              : caregiver.lead_name || "None",
          admin_name: caregiver.admin_name || "None",
        }));
        this.pagination = response.pagination;
      } catch (error) {
        this.$notifyError("Could not load caregivers: " + error);
      } finally {
        this.$spinner.hide();
      }
    },
    getLeads() {
      return caregiversService
        .getLeads()
        .then((leads) => {
          this.leadOptions = leads;
        })
        .catch((error) => {
          this.$notifyError("Could not load leads: " + error);
        });
    },
    getAdmins() {
      return caregiversService
        .getAdmins()
        .then((admins) => {
          this.adminOptions = admins;
        })
        .catch((error) => {
          this.$notifyError("Could not load admins: " + error);
        });
    },
    arePasswordsSame() {
      return this.caregiver.password === this.caregiver.confirmPassword;
    },
    validatePassword(password) {
      // At least 8 characters
      if (password.length < 8) {
        return { valid: false, message: "Password must be at least 8 characters long." };
      }
      // At least one number
      if (!/\d/.test(password)) {
        return { valid: false, message: "Password must contain at least one number." };
      }
      // At least one special character
      if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
        return { valid: false, message: "Password must contain at least one special character." };
      }
      return { valid: true };
    },
    toggleCaregiverStatus(caregiver) {
      const isActive = caregiver.active === 1;
      const newStatus = isActive ? 0 : 1;
      const action = isActive ? "deactivated" : "activated";

      return caregiversService
        .update(caregiver.id, { active: newStatus })
        .then(() => {
          this.$notifySuccess(`Successfully ${action} caregiver!`);
          this.fetchCaregivers(this.pagination.currentPage);
        })
        .catch((error) => {
          this.$notifyError(
            `Error ${action.slice(0, -1)}ing caregiver: ${error}`,
          );
        });
    },
    openAdminPasswordModal(caregiver) {
      this.caregiverForPasswordChange = caregiver;
      this.showAdminPasswordModal = true;
    },
    closeAdminPasswordModal() {
      this.showAdminPasswordModal = false;
      this.caregiverForPasswordChange = null;
    },
    handleAdminPasswordSuccess() {
      this.$notifySuccess('Password changed successfully!');
      this.closeAdminPasswordModal();
    },
    handleRowClick(caregiver) {
      const caregiverName = `${caregiver.first_name} ${caregiver.last_name}`;
      this.$router.push({
        name: "CaregiverDetails",
        params: { id: caregiver.id },
        query: { name: caregiverName },
      });
    },
    onSearch() {
      this.pagination.currentPage = 1;
      this.fetchCaregivers(1);
    },
    clearSearch() {
      this.searchQuery = "";
      this.pagination.currentPage = 1;
      this.fetchCaregivers(1);
    },
    onStatusFilterChange() {
      this.pagination.currentPage = 1;
      this.fetchCaregivers(1);
    },
    onAdminFilterChange() {
      this.pagination.currentPage = 1;
      this.fetchCaregivers(1);
    },
    handleSortChange({ prop, order }) {
      this.sortProp = prop;
      this.sortOrder = order;
      this.fetchCaregivers(1);
    },
    handlePageSizeChange(newSize) {
      this.pagination.perPage = newSize;
      this.pagination.currentPage = 1;
      this.fetchCaregivers(1);
    },
    async saveCaregiver() {
      const caregiverObj = this.caregiver;

      if (!caregiverObj.first_name) {
        this.$notifyError("No caregiver first name!");
        return;
      }
      if (!caregiverObj.last_name) {
        this.$notifyError("No caregiver last name!");
        return;
      }
      if (!caregiverObj.email) {
        this.$notifyError("No caregiver email!");
        return;
      }
      if (!caregiverObj.user_type) {
        this.$notifyError("Please select a user type!");
        return;
      }
      if (caregiverObj.user_type === "Caregiver" && !caregiverObj.id_lead) {
        this.$notifyError("Please select a Lead for this Caregiver!");
        return;
      }
      if (
        caregiverObj.user_type === "Employment Specialist" &&
        !caregiverObj.id_lead
      ) {
        this.$notifyError(
          "Please select a Manager for this Employment Specialist!",
        );
        return;
      }
      if (caregiverObj.position_id && !/^\d+$/.test(caregiverObj.position_id)) {
        this.$notifyError(
          "Position ID must contain only digits (0-9).",
        );
        return;
      }
      if (!this.caregiverBeingEdited) {
        if (!caregiverObj.password) {
          this.$notifyError("No caregiver password!");
          return;
        }
        if (!this.arePasswordsSame()) {
          this.$notifyError("Passwords do not match.");
          return;
        }
        // Validate password requirements
        const passwordValidation = this.validatePassword(caregiverObj.password);
        if (!passwordValidation.valid) {
          this.$notifyError(passwordValidation.message);
          return;
        }
      }

      this.$spinner.show();

      const { schedule, ...caregiverDataWithoutSchedule } = caregiverObj;
      caregiversService
        .create(caregiverDataWithoutSchedule)
        .then(() => {
          this.$notifySuccess("Successfully created caregiver.");
          this.$modal.close("create_or_edit_caregiver");
          return this.fetchCaregivers();
        })
        .catch((error) => {
          // Extract the error message from the API response if available
          const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || error;
          this.$notifyError("Error: " + errorMessage);
          // Modal stays open so user can correct the error
        })
        .finally(() => {
          this.$spinner.hide();
        });
    },
    createCaregiver() {
      this.newCaregiver = {
        first_name: "",
        last_name: "",
        email: "",
        mobile_phone: "",
        other_phone: "",
        password: "",
        user_type: "",
        id_lead: null,
        id_admin: null,
        title: "",
        business_unit: "",
        hourly_pay: "",
        salary: "",
        position_id: "",
        address: {
          address1: "",
          address2: "",
          city: "",
          state: "",
          zip: "",
        },
      };
      this.$modal.open("create_or_edit_caregiver");
    },
    async viewSchedule(caregiver) {
      this.selectedCaregiverName = `${caregiver.first_name} ${caregiver.last_name}`;
      this.selectedCaregiver = caregiver;
      this.loadingShifts = true;
      this.caregiverShifts = [];
      this.activeScheduleTab = "calendar";
      this.selectedCaregiverSchedule = {};
      this.inferredCaregiverTimezone = null;
      this.scheduleTimezone = null;

      // Initialize calendar to current month
      this.currentMonth = new Date();
      this.updateDaysInMonth();

      this.$modal.open("view_caregiver_schedule");

      try {
        this.caregiverShifts = await this.getCaregiverShifts(caregiver.id);

        // Load caregiver's schedule for editing
        await this.loadCaregiverSchedule();

        // Infer timezone from caregiver's address
        if (caregiver.address && caregiver.address.id) {
          try {
            const tzResult = await timezoneService.getFromAddress(caregiver.address.id);
            this.inferredCaregiverTimezone = tzResult?.timezone || null;
            this.scheduleTimezone = tzResult?.timezone || this.scheduleTimezone;
          } catch (e) { /* leave null */ }
        }
        // If schedule already has a timezone, use that
        if (this.selectedCaregiverSchedule.timezone) {
          this.scheduleTimezone = this.selectedCaregiverSchedule.timezone;
          this.inferredCaregiverTimezone = this.selectedCaregiverSchedule.timezone;
        }
      } catch (error) {
        this.$notifyError("Error loading caregiver schedule: " + error);
      } finally {
        this.loadingShifts = false;
      }
    },
    async getCaregiverShifts(caregiverId) {
      // Match the Calendar page's date range calculation for current month view
      const year = this.currentMonth.getFullYear();
      const month = this.currentMonth.getMonth();

      // Get first and last day of the current month being viewed
      const startDate = new Date(year, month, 1);
      const endDate = new Date(year, month + 1, 0, 23, 59, 59, 999);

      const params = {
        startDate: startDate.toISOString().split("T")[0] + "T00:00:00.000Z",
        endDate: endDate.toISOString().split("T")[0] + "T23:59:59.999Z",
        userType: "Caregiver",
        userId: caregiverId,
        perPage: 1000, // Get all shifts for the month
      };

      const response = await shiftsService.getByRole(params);

      return response.shifts.map((shift) => {
        let status = "scheduled";

        // Handle shift_status field (by-role uses shift_status instead of computing from clockIn/clockOut)
        if (shift.shift_status === "Clocked Out") {
          status = "clocked-out";
        } else if (shift.shift_status === "Clocked In") {
          status = "clocked-in";
        } else if (shift.shift_status === "Approved") {
          status = "approved";
        } else if (shift.shift_status === "Missed") {
          status = "missed";
        } else if (shift.shift_status === "Scheduled") {
          status = "scheduled";
        }

        const clientName =
          shift.payer === "Non Service"
            ? "N/A"
            : shift.clients && shift.clients.length > 0
              ? shift.clients[0].name
              : "Unknown Client";

        return {
          id: shift.id,
          date: shift.date,
          startTime: shift.scheduled_start || shift.startTime,
          endTime: shift.scheduled_end || shift.endTime,
          status: status,
          clientName: clientName,
        };
      });
    },
    async saveCaregiverSchedule(caregiverId, scheduleData) {
      try {
        if (!scheduleData.week_start_date) {
          const today = new Date();
          const dayOfWeek = today.getDay();
          const daysUntilMonday = dayOfWeek === 0 ? 1 : 8 - dayOfWeek;
          const nextMonday = new Date(today);
          nextMonday.setDate(today.getDate() + daysUntilMonday);
          scheduleData.week_start_date = localDateString(nextMonday);
        }

        // Set start_date if not already set (required field)
        if (!scheduleData.start_date) {
          scheduleData.start_date = localDateString();
        }

        let savedSchedule;

        if (scheduleData.id) {
          const {
            id,
            createdAt,
            updatedAt,
            clients,
            caregivers,
            ...rawUpdateData
          } = scheduleData;

          // Filter out unnecessary fields and create clean payload
          const updateData = this.createCleanSchedulePayload(
            rawUpdateData,
            caregiverId,
          );

          savedSchedule = await schedulesService.update(id, updateData);
        } else {
          // Create clean payload for new schedule
          const cleanScheduleData = this.createCleanSchedulePayload(
            scheduleData,
            caregiverId,
          );

          savedSchedule = await schedulesService.create(cleanScheduleData);
        }

        return savedSchedule;
      } catch (error) {
        throw error;
      }
    },
    createCleanSchedulePayload(scheduleData, caregiverId) {
      // Start with essential fields
      const cleanPayload = {
        caregiver_id: caregiverId,
      };

      // Add basic schedule fields if they exist
      const basicFields = [
        "start_date",
        "end_date",
        "week_start_date",
        "notes",
        "timezone",
        "recurring",
      ];
      basicFields.forEach((field) => {
        if (scheduleData[field] !== undefined && scheduleData[field] !== null) {
          cleanPayload[field] = scheduleData[field];
        }
      });

      // Only include days that have actual shifts
      const days = [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday",
      ];
      days.forEach((day) => {
        const shiftsKey = `${day}_shifts`;
        const shifts = scheduleData[shiftsKey];

        // Only include if there are shifts with actual start/end times
        if (shifts && Array.isArray(shifts) && shifts.length > 0) {
          const validShifts = shifts.filter(
            (shift) => shift.start && shift.end,
          );
          if (validShifts.length > 0) {
            // Convert ALL times to UTC format
            cleanPayload[shiftsKey] = validShifts.map((shift) => ({
              start: this.convertTimeToUTC(shift.start, day),
              end: this.convertTimeToUTC(shift.end, day),
            }));
          }
        }
      });

      return cleanPayload;
    },
    convertScheduleShiftsToLocal(schedule) {
      if (!schedule) return schedule;

      const convertedSchedule = { ...schedule };
      const days = [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday",
      ];

      days.forEach((day) => {
        const shiftsKey = `${day}_shifts`;
        const shifts = convertedSchedule[shiftsKey];

        if (shifts && Array.isArray(shifts)) {
          convertedSchedule[shiftsKey] = shifts.map((shift) => {
            if (!shift.start || !shift.end) return shift;

            // Convert UTC datetime strings to local datetime strings
            let localStart = shift.start;
            let localEnd = shift.end;

            // If the time includes 'T', treat it as UTC and convert to local
            if (shift.start.includes("T")) {
              // Ensure we treat it as UTC by adding 'Z' if not present
              const utcStartTime = shift.start.endsWith("Z")
                ? shift.start
                : shift.start + "Z";
              const startDate = new Date(utcStartTime);
              if (!isNaN(startDate.getTime())) {
                // Convert to local datetime string (YYYY-MM-DDTHH:mm:ss format)
                const year = startDate.getFullYear();
                const month = String(startDate.getMonth() + 1).padStart(2, "0");
                const date = String(startDate.getDate()).padStart(2, "0");
                const hours = String(startDate.getHours()).padStart(2, "0");
                const minutes = String(startDate.getMinutes()).padStart(2, "0");
                const seconds = String(startDate.getSeconds()).padStart(2, "0");
                localStart = `${year}-${month}-${date}T${hours}:${minutes}:${seconds}`;
              }
            }

            if (shift.end.includes("T")) {
              // Ensure we treat it as UTC by adding 'Z' if not present
              const utcEndTime = shift.end.endsWith("Z")
                ? shift.end
                : shift.end + "Z";
              const endDate = new Date(utcEndTime);
              if (!isNaN(endDate.getTime())) {
                // Convert to local datetime string (YYYY-MM-DDTHH:mm:ss format)
                const year = endDate.getFullYear();
                const month = String(endDate.getMonth() + 1).padStart(2, "0");
                const date = String(endDate.getDate()).padStart(2, "0");
                const hours = String(endDate.getHours()).padStart(2, "0");
                const minutes = String(endDate.getMinutes()).padStart(2, "0");
                const seconds = String(endDate.getSeconds()).padStart(2, "0");
                localEnd = `${year}-${month}-${date}T${hours}:${minutes}:${seconds}`;
              }
            }

            return {
              ...shift,
              start: localStart,
              end: localEnd,
            };
          });
        }
      });

      return convertedSchedule;
    },
    async saveSelectedCaregiverSchedule() {
      if (!this.selectedCaregiver) {
        this.$notifyError("No caregiver selected");
        return;
      }
      if (!this.scheduleTimezone) {
        this.$notifyError("Timezone is required. Please select a timezone.");
        return;
      }

      this.$spinner.show();
      try {
        this.selectedCaregiverSchedule.timezone = this.scheduleTimezone;
        await this.saveCaregiverSchedule(
          this.selectedCaregiver.id,
          this.selectedCaregiverSchedule,
        );
        this.$notifySuccess("Schedule saved successfully!");

        // Reload all schedules to reflect the changes
        await this.loadCaregiverSchedule();

        // Refresh the calendar view
        this.caregiverShifts = await this.getCaregiverShifts(
          this.selectedCaregiver.id,
        );
        this.activeScheduleTab = "calendar";
      } catch (error) {
        this.$notifyError("Error saving schedule: " + error);
      } finally {
        this.$spinner.hide();
      }
    },
    handleScheduleTabClick(tab) {
      // Tab click handler if needed for future functionality
    },
    togglePasswordVisibility() {
      this.passwordVisible = !this.passwordVisible;
    },
    toggleConfirmPasswordVisibility() {
      this.confirmPasswordVisible = !this.confirmPasswordVisible;
    },
    cancelScheduleEdit() {
      this.activeScheduleTab = "calendar";
      // Reset schedule data to original state
      if (this.selectedCaregiver) {
        this.loadCaregiverSchedule();
      }
    },
    async loadCaregiverSchedule() {
      if (!this.selectedCaregiver) return;

      try {
        // Calculate date range for current month view
        // Format dates directly to avoid timezone conversion issues
        const year = this.currentMonth.getFullYear();
        const month = this.currentMonth.getMonth();
        const lastDay = new Date(year, month + 1, 0).getDate();
        const startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
        const endDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

        const response = await schedulesService.getByCaregiver(
          this.selectedCaregiver.id,
          { startDate, endDate }
        );

        // Handle new response structure with schedules and approvedTimeOff
        this.caregiverSchedules = response.schedules || [];
        this.caregiverApprovedTimeOff = response.approvedTimeOff || [];

        // Sort schedules by start_date
        this.caregiverSchedules.sort((a, b) => {
          if (!a.start_date) return 1;
          if (!b.start_date) return -1;
          return new Date(a.start_date) - new Date(b.start_date);
        });

        if (this.caregiverSchedules.length > 0) {
          // Find the first active or future schedule (not expired)
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          const activeScheduleIndex = this.caregiverSchedules.findIndex((schedule) => {
            if (!schedule.end_date) return true;
            const endDate = new Date(schedule.end_date);
            endDate.setHours(23, 59, 59, 999);
            return endDate >= today;
          });

          // Default to the first active schedule, or first schedule if none are active
          this.selectedScheduleIndex = activeScheduleIndex >= 0 ? activeScheduleIndex : 0;
          // Convert UTC shift times to local time for editing
          this.selectedCaregiverSchedule = this.convertScheduleShiftsToLocal({
            ...this.caregiverSchedules[this.selectedScheduleIndex],
          });
        } else {
          this.selectedScheduleIndex = 0;
          this.selectedCaregiverSchedule = {};
        }

        // Initialize schedule properties for reactivity
        this.initializeScheduleProperties();
      } catch (error) {
        this.caregiverSchedules = [];
        this.caregiverApprovedTimeOff = [];
        this.selectedCaregiverSchedule = {};
        this.selectedScheduleIndex = 0;
        this.initializeScheduleProperties();
      }
    },

    // Calendar navigation methods
    updateDaysInMonth() {
      const year = this.currentMonth.getFullYear();
      const month = this.currentMonth.getMonth();
      const arr = [];
      const first = new Date(year, month, 1);
      const firstWeekday = first.getDay();
      // Add trailing days from previous month
      for (let i = 0; i < firstWeekday; i++) {
        arr.push(new Date(year, month, i - firstWeekday + 1));
      }
      // Add all days of current month
      const d = new Date(year, month, 1);
      while (d.getMonth() === month) {
        arr.push(new Date(d));
        d.setDate(d.getDate() + 1);
      }
      // Add leading days from next month to complete grid
      const total = arr.length;
      const trailing = (7 - (total % 7)) % 7;
      for (let i = 1; i <= trailing; i++) {
        arr.push(new Date(year, month + 1, i));
      }
      this.daysInMonth = arr;
    },
    goToPreviousMonth() {
      const nm = new Date(this.currentMonth);
      nm.setMonth(nm.getMonth() - 1);
      this.currentMonth = nm;
      this.updateDaysInMonth();
      // Reload shifts for the new month
      if (this.selectedCaregiver) {
        this.reloadCaregiverShifts();
      }
    },
    goToNextMonth() {
      const nm = new Date(this.currentMonth);
      nm.setMonth(nm.getMonth() + 1);
      this.currentMonth = nm;
      this.updateDaysInMonth();
      // Reload shifts for the new month
      if (this.selectedCaregiver) {
        this.reloadCaregiverShifts();
      }
    },
    async reloadCaregiverShifts() {
      if (this.selectedCaregiver) {
        this.loadingShifts = true;
        try {
          const shifts = await this.getCaregiverShifts(
            this.selectedCaregiver.id,
          );
          this.caregiverShifts = shifts;
          // Also reload schedules and time off for the new month
          await this.loadCaregiverSchedule();
        } catch (error) {
          this.$notifyError("Error loading caregiver schedule: " + error);
        } finally {
          this.loadingShifts = false;
        }
      }
    },
    shiftsForDate(dateObj) {
      const y = dateObj.getFullYear();
      const m = String(dateObj.getMonth() + 1).padStart(2, "0");
      const d = String(dateObj.getDate()).padStart(2, "0");
      const dateStr = `${y}-${m}-${d}`;

      // Map shift data to format expected by MonthView
      return this.caregiverShifts
        .filter((shift) => shift.date === dateStr)
        .map((shift) => {
          // Determine the status class
          let statusClass = "status-scheduled";
          if (shift.status === "missed") {
            statusClass = "status-missed";
          } else if (shift.status === "approved") {
            statusClass = "status-approved";
          } else if (shift.status === "clocked-out") {
            statusClass = "status-clocked-out";
          } else if (shift.status === "clocked-in") {
            statusClass = "status-clocked-in";
          }

          // Determine client display - show "Multiple" for group shifts with multiple clients
          let clientDisplay = shift.clientName || "Unassigned";
          if (shift.clients && shift.clients.length > 1) {
            clientDisplay = "Multiple";
          }

          // Determine caregiver display - show "Multiple" for group shifts with multiple caregivers
          let caregiverDisplay = shift.caregiver || "Unassigned";
          if (shift.caregivers && shift.caregivers.length > 1) {
            caregiverDisplay = "Multiple";
          }

          return {
            ...shift,
            formattedStartTime: this.formatTime(shift.startTime),
            formattedEndTime: this.formatTime(shift.endTime),
            client: clientDisplay,
            caregiver: caregiverDisplay,
            statusClass: statusClass,
            payer: shift.payer || "DSPD",
            shiftType: shift.shiftType || "1-on-1",
            hasClockIn:
              shift.status === "clocked-in" || shift.status === "clocked-out",
            hasClockOut: shift.status === "clocked-out",
          };
        });
    },
    isToday(dateObj) {
      const now = new Date();
      return (
        dateObj.getFullYear() === now.getFullYear() &&
        dateObj.getMonth() === now.getMonth() &&
        dateObj.getDate() === now.getDate()
      );
    },
    viewShiftDetails(shift) {
      // Navigate to Shifts page with this shift selected
      this.$router.push({
        name: "Shifts",
        query: { shiftId: shift.id },
      });
    },
    formatTime(timeString) {
      if (!timeString) return "";

      // Handle ISO 8601 datetime strings (e.g., "2025-10-15T15:00:00.000Z")
      if (timeString.includes("T")) {
        const date = new Date(timeString);
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? "pm" : "am";
        let displayHours = hours % 12;
        displayHours = displayHours ? displayHours : 12;
        return `${displayHours}:${minutes.toString().padStart(2, "0")}${ampm}`;
      }

      // Handle time-only strings (e.g., "15:00")
      const timeParts = timeString.split(":");
      let hours = parseInt(timeParts[0]);
      const minutes = timeParts[1] || "00";
      const ampm = hours >= 12 ? "pm" : "am";
      hours = hours % 12;
      hours = hours ? hours : 12;
      return `${hours}:${minutes}${ampm}`;
    },
    getActiveScheduleForDate(dateObj) {
      // Find the schedule that should apply to this specific date
      const currentDate = dateObj.toISOString().split("T")[0]; // Format: YYYY-MM-DD

      // Find all schedules that are active for this date
      const activeSchedules = this.caregiverSchedules.filter((schedule) => {
        const startDate = schedule.start_date;
        const endDate = schedule.end_date;

        // Check if date is within the schedule's range
        const afterStart = !startDate || currentDate >= startDate;
        const beforeEnd = !endDate || currentDate <= endDate;

        return afterStart && beforeEnd;
      });

      if (activeSchedules.length === 0) return null;

      // If multiple schedules apply, use the one with the latest start date (most recent)
      activeSchedules.sort((a, b) => {
        if (!a.start_date && !b.start_date) return 0;
        if (!a.start_date) return -1;
        if (!b.start_date) return 1;
        return new Date(b.start_date) - new Date(a.start_date);
      });

      return this.convertScheduleShiftsToLocal({ ...activeSchedules[0] });
    },

    getScheduledHoursForDate(dateObj) {
      // Get the appropriate schedule for this specific date
      const activeSchedule = this.getActiveScheduleForDate(dateObj);
      if (!activeSchedule) return null;

      const dayOfWeek = dateObj.getDay();
      const dayNames = [
        "sunday",
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
      ];
      const dayKey = dayNames[dayOfWeek];

      // Check for new format with day_shifts arrays
      const dayShifts = activeSchedule[`${dayKey}_shifts`];
      if (dayShifts && dayShifts.length > 0) {
        // Handle multiple shifts per day - return all shifts
        const validShifts = dayShifts.filter(
          (shift) => shift.start && shift.end,
        );
        if (validShifts.length > 0) {
          return validShifts.map((shift) => {
            // Convert UTC times to local times for display
            const startDate = new Date(shift.start);
            const endDate = new Date(shift.end);

            const startTime = `${String(startDate.getHours()).padStart(2, "0")}:${String(startDate.getMinutes()).padStart(2, "0")}`;
            const endTime = `${String(endDate.getHours()).padStart(2, "0")}:${String(endDate.getMinutes()).padStart(2, "0")}`;

            return {
              start: this.formatTime(startTime),
              end: this.formatTime(endTime),
              dayKey: dayKey,
            };
          });
        }
      }

      // Fallback to legacy format for backwards compatibility
      // Check if this day is enabled in the schedule
      if (!activeSchedule[`${dayKey}_enabled`]) {
        return null;
      }

      const startTime = activeSchedule[`${dayKey}_start`];
      const endTime = activeSchedule[`${dayKey}_end`];

      if (!startTime || !endTime) return null;

      return [
        {
          start: this.formatTime(startTime),
          end: this.formatTime(endTime),
          dayKey: dayKey,
        },
      ];
    },
    onDayEnabledChange(dayKey, value) {
      // Update the schedule directly
      this.$set(this.selectedCaregiverSchedule, dayKey + "_enabled", value);

      // Clear shifts if disabling, or add empty shift if enabling
      const shiftsKey = dayKey + "_shifts";
      if (!value) {
        this.$set(this.selectedCaregiverSchedule, shiftsKey, []);
      } else if (
        !this.selectedCaregiverSchedule[shiftsKey] ||
        this.selectedCaregiverSchedule[shiftsKey].length === 0
      ) {
        this.$set(this.selectedCaregiverSchedule, shiftsKey, [
          { start: null, end: null },
        ]);
      }
    },
    initializeScheduleProperties() {
      // Set default start_date if not exists
      if (!this.selectedCaregiverSchedule.start_date) {
        this.$set(
          this.selectedCaregiverSchedule,
          "start_date",
          localDateString(),
        );
      }

      // Ensure all day properties exist for reactivity
      this.scheduleEditorDays.forEach((day) => {
        if (
          !this.selectedCaregiverSchedule.hasOwnProperty(day.key + "_enabled")
        ) {
          this.$set(
            this.selectedCaregiverSchedule,
            day.key + "_enabled",
            false,
          );
        }

        // Initialize shifts array if not exists
        const shiftsKey = day.key + "_shifts";
        if (!this.selectedCaregiverSchedule.hasOwnProperty(shiftsKey)) {
          this.$set(this.selectedCaregiverSchedule, shiftsKey, []);
        }
      });

      // Set enabled days based on existing shifts
      this.scheduleEditorDays.forEach((day) => {
        const hasShifts =
          this.selectedCaregiverSchedule[day.key + "_shifts"] &&
          this.selectedCaregiverSchedule[day.key + "_shifts"].length > 0 &&
          this.selectedCaregiverSchedule[day.key + "_shifts"].some(
            (shift) => shift.start && shift.end,
          );

        if (hasShifts) {
          this.$set(this.selectedCaregiverSchedule, day.key + "_enabled", true);
        }
      });
    },

    // Multiple shifts management methods
    getShiftsForDay(day) {
      const shiftsKey = `${day}_shifts`;

      if (
        this.selectedCaregiverSchedule[shiftsKey] &&
        Array.isArray(this.selectedCaregiverSchedule[shiftsKey])
      ) {
        return this.selectedCaregiverSchedule[shiftsKey];
      }

      // Return empty shift if day is enabled but no times set
      if (this.selectedCaregiverSchedule[`${day}_enabled`]) {
        return [{ start: null, end: null }];
      }

      return [];
    },

    updateShiftTime(day, shiftIndex, timeType, value) {
      const shiftsKey = `${day}_shifts`;

      // Initialize shifts array if it doesn't exist
      if (!this.selectedCaregiverSchedule[shiftsKey]) {
        this.$set(this.selectedCaregiverSchedule, shiftsKey, []);
      }

      // Ensure the shift exists
      while (this.selectedCaregiverSchedule[shiftsKey].length <= shiftIndex) {
        this.selectedCaregiverSchedule[shiftsKey].push({
          start: null,
          end: null,
        });
      }

      // Convert time to datetime string before storing
      const dateTimeValue = value
        ? this.convertTimeToDateTime(value, day)
        : null;

      // Update the specific time
      this.$set(
        this.selectedCaregiverSchedule[shiftsKey][shiftIndex],
        timeType,
        dateTimeValue,
      );
    },

    addShift(day) {
      const shiftsKey = `${day}_shifts`;

      if (!this.selectedCaregiverSchedule[shiftsKey]) {
        this.$set(this.selectedCaregiverSchedule, shiftsKey, []);
      }

      this.selectedCaregiverSchedule[shiftsKey].push({
        start: null,
        end: null,
      });
    },

    removeShift(day, shiftIndex) {
      const shiftsKey = `${day}_shifts`;

      if (
        this.selectedCaregiverSchedule[shiftsKey] &&
        this.selectedCaregiverSchedule[shiftsKey].length > 1
      ) {
        this.selectedCaregiverSchedule[shiftsKey].splice(shiftIndex, 1);
      }
    },

    // Schedule management methods
    formatScheduleLabel(schedule) {
      if (!schedule.start_date) return "Current Schedule";

      // start_date/end_date are DATE-only strings — parse as local dates so
      // labels don't shift to the previous day (see utils/dateOnly.js).
      const startDate = parseDateOnly(schedule.start_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset to start of day for accurate comparison

      // Check if schedule has ended
      if (schedule.end_date) {
        const endDate = parseDateOnly(schedule.end_date);
        endDate.setHours(23, 59, 59, 999); // Set to end of day

        if (endDate < today) {
          return `Ended ${endDate.toLocaleDateString()}`;
        }
      }

      if (startDate <= today) {
        return schedule.end_date
          ? `Current (until ${parseDateOnly(schedule.end_date).toLocaleDateString()})`
          : "Current Schedule";
      } else {
        return `Starts ${startDate.toLocaleDateString()}`;
      }
    },

    selectSchedule(index) {
      this.selectedScheduleIndex = index;
      // Convert UTC shift times to local time for editing
      this.selectedCaregiverSchedule = this.convertScheduleShiftsToLocal({
        ...this.caregiverSchedules[index],
      });
      this.initializeScheduleProperties();
    },

    createNewSchedule() {
      this.selectedCaregiverSchedule = {};
      this.selectedScheduleIndex = -1;
      this.initializeScheduleProperties();
    },

    async duplicateCurrentSchedule() {
      if (!this.selectedCaregiverSchedule.id) return;

      const duplicatedSchedule = { ...this.selectedCaregiverSchedule };
      delete duplicatedSchedule.id;
      delete duplicatedSchedule.createdAt;
      delete duplicatedSchedule.updatedAt;

      // Set start date to two weeks from now by default
      const futureDate = new Date();
      futureDate.setDate(futureDate.getDate() + 14);
      duplicatedSchedule.start_date = localDateString(futureDate);
      duplicatedSchedule.end_date = null;

      this.selectedCaregiverSchedule = duplicatedSchedule;
      this.selectedScheduleIndex = -1;
      this.initializeScheduleProperties();

      this.$notifySuccess("Schedule duplicated. Adjust dates and save.");
    },

    async deleteCurrentSchedule() {
      if (
        !this.selectedCaregiverSchedule.id ||
        this.caregiverSchedules.length <= 1
      )
        return;

      if (!confirm("Are you sure you want to delete this schedule?")) return;

      try {
        await schedulesService.delete(this.selectedCaregiverSchedule.id);
        await this.loadCaregiverSchedule();
        this.$notifySuccess("Schedule deleted successfully");
      } catch (error) {
        this.$notifyError("Error deleting schedule: " + error);
      }
    },

    // Helper methods for datetime handling
    extractTimeFromDateTime(dateTimeString) {
      if (!dateTimeString) return null;

      // If it's already in HH:mm format, return as-is
      if (
        typeof dateTimeString === "string" &&
        dateTimeString.match(/^\d{2}:\d{2}$/)
      ) {
        return dateTimeString;
      }

      // If it's a full datetime string, extract time portion
      try {
        const date = new Date(dateTimeString);
        if (isNaN(date.getTime())) return null;

        // Format as HH:mm in local time
        return date.toLocaleTimeString("en-US", {
          hour12: false,
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch (error) {
        return null;
      }
    },

    convertTimeToDateTime(timeString, day, baseDate = null) {
      if (!timeString) return null;

      // Use the schedule's start date or current date as base
      const base =
        baseDate ||
        this.selectedCaregiverSchedule.start_date ||
        localDateString();

      // Create a Date object with the time on the base date and convert to UTC
      const localDateTime = new Date(`${base}T${timeString}`);
      return localDateTime.toISOString();
    },

    convertTimeToUTC(timeString, day) {
      if (!timeString) return null;

      // If it's already in UTC format (contains 'Z' at the end), return as-is
      if (timeString.includes("Z")) {
        return timeString;
      }

      // If it's a full datetime string (contains 'T'), convert it to UTC
      if (timeString.includes("T")) {
        const localDateTime = new Date(timeString);
        return localDateTime.toISOString();
      }

      // If it's just a time string (HH:mm format), use the convertTimeToDateTime method
      return this.convertTimeToDateTime(timeString, day);
    },
  },
};
</script>

<style scoped>
.caregiver-edit-modal :deep(.el-dialog__body) {
  height: calc(80vh - 120px) !important;
  overflow-y: auto !important;
  padding: 20px !important;
}

.caregiver-schedule-modal :deep(.el-dialog__body) {
  height: 80vh !important;
  overflow-y: auto !important;
  padding: 20px !important;
}
.schedule-calendar-container {
  width: 100%;
  margin: 0;
}

.schedule-legend {
  display: flex;
  justify-content: center;
  gap: 2rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 8px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.legend-color {
  width: 20px;
  height: 20px;
  border-radius: 4px;
}

.legend-color.scheduled {
  background-color: #17a2b8;
}

.legend-color.clocked-in {
  background-color: #28a745;
}

.legend-color.clocked-out {
  background-color: #6c757d;
}

.calendar-view {
  margin-top: 1rem;
}

/* Password styling to match AIVRow */
.password-input-wrapper {
  position: relative;
}

.password-toggle-icon {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  cursor: pointer;
  color: #ffffff !important;
  z-index: 10;
  font-size: 16px;
}

.password-toggle-icon:hover {
  color: #ffffff !important;
}

.password-requirements {
  font-size: 0.9em;
  color: grey;
  margin-top: 8px;
}

.password-requirements ul {
  margin-bottom: 0;
  padding-left: 1.2rem;
}

/* Match AIVRow label styling */
label.d-block {
  margin-bottom: 2px !important;
}

/* Schedule type toggle section styles - matching ShiftModal */
.schedule-type-toggle-section {
  margin-bottom: 1.5rem;
  padding: 0;
  background-color: transparent;
  border: none;
}

.button-toggle-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  margin: 0;
}

.toggle-button {
  border-radius: 0;
  min-width: 120px;
  margin: 0;
}

.toggle-button:first-child {
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
}

.toggle-button:last-child {
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
  border-left: none;
}

:deep(.toggle-button.el-button--default) {
  background-color: #606266;
  border-color: #606266;
  color: #fff;
}

:deep(.toggle-button.el-button--default:hover) {
  background-color: #737373;
  border-color: #737373;
  color: #fff;
}

:deep(.toggle-button.el-button--primary) {
  background-color: #409eff;
  border-color: #409eff;
  color: #fff;
}

.schedule-view {
  margin-top: 1rem;
}

/* Modern Calendar Styling - Matching Clients page */
.modern-calendar-wrapper {
  width: 100%;
  max-width: none;
  margin: 0;
}

/* Modern Legend Row - Matching Calendar page */
.modern-legend-row {
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.legend-groups {
  display: flex;
  align-items: center;
  gap: 2rem;
  flex-wrap: wrap;
  justify-content: center;
}

.legend-group {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.legend-group-header {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: #6ba6cd;
  letter-spacing: 0.025em;
  text-transform: uppercase;
  flex-shrink: 0;
}

.legend-group-header i {
  font-size: 0.75rem;
  width: 12px;
  text-align: center;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.9);
  padding: 0.25rem 0;
  transition: color 0.2s ease;
}

.legend-item:hover {
  color: #fff;
}

.legend-color {
  width: 14px;
  height: 10px;
  border-radius: 2px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s ease;
}

.legend-item:hover .legend-color {
  transform: scale(1.1);
}

/* Status Colors - Matching Calendar page */
.legend-color.status-scheduled {
  background: linear-gradient(
    135deg,
    rgba(13, 110, 253, 0.8),
    rgba(13, 110, 253, 0.6)
  );
}

.legend-color.status-clocked-in {
  background: linear-gradient(
    135deg,
    rgba(255, 193, 7, 0.9),
    rgba(255, 193, 7, 0.7)
  );
}

.legend-color.status-clocked-out {
  background: linear-gradient(
    135deg,
    rgba(237, 137, 54, 0.7),
    rgba(237, 137, 54, 0.5)
  );
}

.legend-color.status-approved {
  background: linear-gradient(
    135deg,
    rgba(156, 39, 176, 0.7),
    rgba(156, 39, 176, 0.5)
  );
}

.legend-color.status-missed {
  background: linear-gradient(
    135deg,
    rgba(108, 117, 125, 0.8),
    rgba(108, 117, 125, 0.6)
  );
}

.legend-color.work-schedule {
  background: linear-gradient(
    135deg,
    rgba(64, 158, 255, 0.3),
    rgba(64, 158, 255, 0.15)
  );
  border: 1px solid rgba(64, 158, 255, 0.5);
}

.legend-color.time-off {
  background: rgba(255, 152, 0, 0.2);
  border: 1px solid rgba(255, 152, 0, 0.6);
}

/* Month Navigation */
.month-navigation {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  margin: 1rem 0;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.8);
  padding: 0;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 36px;
  width: 36px;
}

.nav-btn:hover {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.25);
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.nav-btn i {
  font-size: 12px;
}

.current-month-display {
  font-size: 1.25rem;
  font-weight: 600;
  color: #fff;
  min-width: 200px;
  text-align: center;
  letter-spacing: 0.5px;
}

/* Calendar-specific styles for Caregiver modal */
:deep(.custom-calendar) {
  width: 100%;
}

:deep(.calendar-grid) {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
}

:deep(.day-name) {
  font-weight: bold;
  text-align: center;
  border-right: 1px solid rgba(255, 255, 255, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  padding: 8px;
  color: #fff;
  background: rgba(255, 255, 255, 0.05);
}

:deep(.calendar-day) {
  position: relative;
  background: transparent;
  border-right: 1px solid rgba(255, 255, 255, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  min-height: 80px;
  padding: 4px;
  cursor: pointer;
}

:deep(.calendar-day:nth-child(7n)) {
  border-right: none;
}

:deep(.calendar-day:nth-last-child(-n + 7)) {
  border-bottom: none;
}

:deep(.day-name:nth-child(7n)) {
  border-right: none;
}

:deep(.date-number) {
  font-size: 0.85rem;
  font-weight: bold;
  color: #fff;
}

:deep(.today) {
  background-color: rgba(107, 166, 205, 0.2);
  border: 2px solid #6ba6cd;
}

:deep(.other-month) {
  opacity: 0.4;
}

:deep(.shift-item) {
  margin-top: 2px;
  font-size: 0.7rem;
  color: #fff;
  padding: 4px 6px;
  min-height: 24px;
  border-radius: 3px;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 4px;
}

:deep(.shift-time) {
  font-weight: bold;
  flex-shrink: 0;
}

:deep(.shift-client) {
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: 0.9;
}

:deep(.shift-caregiver) {
  font-size: 0.6rem;
  color: #e0e0e0;
  font-style: italic;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  opacity: 0.8;
}

.calendar-view {
  width: 100%;
  margin-top: 1rem;
}

@media (max-width: 768px) {
  .modern-legend-row {
    padding: 0.5rem;
  }

  .legend-groups {
    gap: 1rem;
  }

  .month-navigation {
    gap: 1rem;
    padding: 0.5rem;
  }

  .current-month-display {
    font-size: 1rem;
    min-width: 150px;
  }

  .calendar-view {
    padding: 0.25rem;
  }

  :deep(.calendar-day) {
    min-height: 80px !important;
    padding: 4px !important;
  }

  :deep(.shift-item) {
    font-size: 0.6rem !important;
    padding: 2px 3px !important;
  }

  :deep(.shift-client) {
    display: none !important;
  }

  :deep(.shift-caregiver) {
    display: none !important;
  }

  :deep(.day-name) {
    font-size: 0.65rem !important;
    padding: 6px 2px !important;
  }

  :deep(.date-number) {
    font-size: 0.75rem !important;
  }

  .password-row .col-md-4 {
    margin-bottom: 15px;
  }
}

@media (max-width: 480px) {
  :deep(.calendar-day) {
    min-height: 60px !important;
    padding: 2px !important;
  }

  :deep(.shift-time) {
    font-size: 0.55rem !important;
  }
}

/* Schedule Editor Styles - Calendar-like layout */
.schedule-editor {
  padding: 1rem;
  background: transparent;
  border-radius: 8px;
}

/* Schedule Management Section */
.schedule-management {
  margin-bottom: 1.5rem;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.03);
}

.schedule-selector {
  margin-bottom: 1rem;
}

.schedule-tabs {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 0.5rem;
}

.schedule-tab {
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  color: #fff;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
}

.schedule-tab:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.3);
}

.schedule-tab.active {
  background: #409eff;
  border-color: #409eff;
  color: #fff;
}

.schedule-tab.new-schedule {
  background: rgba(40, 167, 69, 0.2);
  border-color: rgba(40, 167, 69, 0.4);
  color: #28a745;
}

.schedule-tab.new-schedule:hover {
  background: rgba(40, 167, 69, 0.3);
  border-color: rgba(40, 167, 69, 0.6);
}

.schedule-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.action-btn {
  padding: 0.4rem 0.8rem;
  font-size: 0.75rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.3rem;
}

.duplicate-btn {
  background: rgba(23, 162, 184, 0.2);
  color: #17a2b8;
  border: 1px solid rgba(23, 162, 184, 0.4);
}

.duplicate-btn:hover {
  background: rgba(23, 162, 184, 0.3);
  border-color: rgba(23, 162, 184, 0.6);
}

.delete-btn {
  background: rgba(220, 53, 69, 0.2);
  color: #dc3545;
  border: 1px solid rgba(220, 53, 69, 0.4);
}

.delete-btn:hover {
  background: rgba(220, 53, 69, 0.3);
  border-color: rgba(220, 53, 69, 0.6);
}

/* Date Range Section */
.schedule-date-range {
  margin-bottom: 1.5rem;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.03);
}

.date-range-grid {
  display: flex;
  gap: 1.5rem;
  align-items: flex-end;
}

.date-input-group {
  flex: 1;
}

.date-input {
  width: 100%;
  height: 32px;
  padding: 0 8px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  color: #fff;
  font-size: 12px;
  margin-top: 4px;
}

.date-input:hover {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.3);
}

.date-input:focus {
  outline: none;
  border-color: #409eff;
  background: rgba(255, 255, 255, 0.15);
}

.date-input::-webkit-calendar-picker-indicator {
  filter: invert(1);
  opacity: 0.7;
}

.date-input::-webkit-calendar-picker-indicator:hover {
  opacity: 1;
}

/* Calendar-style grid matching Calendar page */
.schedule-calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: transparent;
}

/* Day headers - matching Calendar page styling */
.schedule-day-header {
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  border-right: 1px solid rgba(255, 255, 255, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  padding: 8px;
  color: #fff;
  background: rgba(255, 255, 255, 0.05);
  font-size: 0.875rem;
}

.schedule-day-header:nth-child(7n) {
  border-right: none;
}

/* Day cells - matching Calendar page */
.schedule-day-cell {
  position: relative;
  background: transparent;
  border-right: 1px solid rgba(255, 255, 255, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  min-height: 120px;
  padding: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.schedule-day-cell:nth-child(7n + 7) {
  border-right: none;
}

.schedule-day-cell:hover {
  background: rgba(255, 255, 255, 0.03);
}

.day-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  height: 100%;
  align-items: center;
  justify-content: center;
  padding: 8px;
}

.day-content.day-enabled {
  background: rgba(64, 158, 255, 0.05);
  border: 1px solid rgba(64, 158, 255, 0.2);
  border-radius: 4px;
}

/* Checkbox styling */
.header-checkbox-label {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  cursor: pointer;
  position: relative;
}

.header-checkbox-label span:first-child {
  font-weight: bold;
  color: #fff;
}

.header-checkbox-label input[type="checkbox"] {
  opacity: 0;
  position: absolute;
  cursor: pointer;
}

.schedule-checkmark {
  height: 16px;
  width: 16px;
  background-color: transparent;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-radius: 3px;
  position: relative;
  transition: all 0.2s ease;
}

.header-checkbox-label:hover .schedule-checkmark {
  border-color: #409eff;
  background: rgba(64, 158, 255, 0.1);
}

.header-checkbox-label input:checked ~ .schedule-checkmark {
  background-color: #409eff;
  border-color: #409eff;
}

.header-checkbox-label input:checked ~ .schedule-checkmark:after {
  content: "";
  position: absolute;
  display: block;
  left: 4px;
  top: 1px;
  width: 3px;
  height: 7px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

/* Time controls */
.time-controls {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  align-items: center;
}

.time-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: #409eff;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0;
}

.time-input-group {
  width: 100%;
}

.day-disabled-text {
  color: rgba(255, 255, 255, 0.3);
  font-size: 0.85rem;
  font-style: italic;
}

/* Multiple Shifts Controls */
.multiple-shifts-controls {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
}

.shift-row {
  display: flex;
  align-items: flex-end;
  gap: 0.25rem;
  padding: 0.25rem;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.shift-times {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
}

.remove-shift-btn {
  background: rgba(220, 53, 69, 0.2);
  color: #dc3545;
  border: 1px solid rgba(220, 53, 69, 0.4);
  border-radius: 3px;
  padding: 2px 6px;
  font-size: 0.7rem;
  cursor: pointer;
  transition: all 0.2s;
  height: 22px;
  min-width: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.remove-shift-btn:hover {
  background: rgba(220, 53, 69, 0.3);
  border-color: rgba(220, 53, 69, 0.6);
}

.add-shift-btn {
  background: rgba(40, 167, 69, 0.2);
  color: #28a745;
  border: 1px solid rgba(40, 167, 69, 0.4);
  border-radius: 4px;
  padding: 0.3rem 0.5rem;
  font-size: 0.7rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  justify-content: center;
  margin-top: 0.25rem;
}

.add-shift-btn:hover {
  background: rgba(40, 167, 69, 0.3);
  border-color: rgba(40, 167, 69, 0.6);
}

:deep(.time-input-group .el-input--mini .el-input__inner) {
  height: 28px !important;
  line-height: 28px !important;
  font-size: 11px !important;
  padding: 0 8px !important;
  background: rgba(255, 255, 255, 0.08) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  color: #fff !important;
  border-radius: 4px !important;
}

:deep(.time-input-group .el-input--mini .el-input__inner:hover) {
  background: rgba(255, 255, 255, 0.12) !important;
  border-color: rgba(255, 255, 255, 0.3) !important;
}

:deep(.time-input-group .el-input--mini .el-input__inner:focus) {
  border-color: #409eff !important;
  background: rgba(255, 255, 255, 0.15) !important;
}

:deep(.time-input-group .el-input--mini .el-input__inner::placeholder) {
  color: rgba(255, 255, 255, 0.4) !important;
  font-size: 11px !important;
}

.notes-section label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #fff;
}

/* Responsive design - Keep 7 column layout */
@media (max-width: 1024px) {
  .schedule-calendar-grid {
    grid-template-columns: repeat(7, 1fr);
  }

  .schedule-day-cell {
    min-height: 100px;
  }

  .schedule-day-header {
    font-size: 0.75rem;
    padding: 6px 4px;
  }

  .header-checkbox-label {
    gap: 6px;
  }

  .header-checkbox-label span:first-child {
    font-size: 0.7rem;
  }

  .schedule-checkmark {
    height: 14px;
    width: 14px;
  }

  :deep(.time-input-group .el-input--mini .el-input__inner) {
    font-size: 10px !important;
    padding: 0 4px !important;
  }
}

@media (max-width: 768px) {
  .schedule-calendar-grid {
    grid-template-columns: repeat(7, 1fr);
    font-size: 0.75rem;
  }

  .schedule-day-cell {
    min-height: 90px;
    padding: 4px;
  }

  .schedule-day-header {
    font-size: 0.65rem;
    padding: 6px 2px;
  }

  .header-checkbox-label {
    gap: 4px;
  }

  .header-checkbox-label span:first-child {
    font-size: 0.6rem;
  }

  :deep(.time-input-group .el-input--mini .el-input__inner) {
    height: 24px !important;
    line-height: 24px !important;
    font-size: 9px !important;
    padding: 0 2px !important;
  }

  :deep(.time-input-group .el-input--mini .el-input__inner::placeholder) {
    font-size: 9px !important;
  }

  .time-controls {
    gap: 0.25rem;
  }
}

@media (max-width: 480px) {
  .date-range-grid {
    flex-direction: column;
    gap: 1rem;
  }

  .schedule-date-range {
    padding: 0.75rem;
  }

  .date-input {
    height: 28px;
    font-size: 11px;
  }

  .schedule-calendar-grid {
    grid-template-columns: repeat(7, 1fr);
    overflow-x: auto;
    min-width: 420px;
  }

  .schedule-editor {
    padding: 0.5rem;
    overflow-x: auto;
  }

  .schedule-day-cell {
    min-height: 80px;
    min-width: 60px;
  }

  .schedule-day-header {
    font-size: 0.6rem;
    padding: 4px 2px;
    min-width: 60px;
  }
}

/* Compact filter section */
.filter-section {
  margin-bottom: 1.5rem;
}

.filter-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 0;
}

.filter-group {
  display: flex;
  align-items: center;
  min-width: 0;
  flex-shrink: 0;
}

.filter-group.search-group {
  flex: 1;
  max-width: 400px;
}

.search-input {
  width: 100%;
}

.compact-filter {
  min-width: 150px;
  width: 150px;
}

@media (max-width: 768px) {
  .filter-row {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }

  .filter-group.search-group {
    max-width: unset;
  }

  .compact-filter {
    width: 100%;
    min-width: unset;
  }
}
</style>
